import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useEmployeeGoalCreate } from "@/queries/employees/use-employee-goals.query.ts";
import useEmployeeSearchInline, { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useGoal, useGoalSave, useGoalsSearchInline } from "@/queries/goals.query.ts";
import { useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useTeamSearchInline } from "@/queries/use-teams.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiDepartment } from "@/types/department.types.ts";
import {
  ApiEmployeeShort,
  getActiveEmployeeLifecycleStatuses,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import {
  ApiGoal,
  ApiGoalConfidenceLevel,
  ApiGoalOwnerType,
  ApiGoalPayload,
  ApiGoalStatus,
} from "@/types/goals.types.ts";
import { ApiOrganization } from "@/types/organization.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiTeam } from "@/types/team.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Multiselect,
  Radio,
  Select,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const CUSTOM_PERIOD = "custom";

interface PeriodOption {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
}

function getQuarterDates(year: number, quarter: number): { start: Date; end: Date } {
  const startMonth = (quarter - 1) * 3;
  return {
    start: new Date(year, startMonth, 1),
    end: new Date(year, startMonth + 3, 0),
  };
}

function getHalfDates(year: number, half: number): { start: Date; end: Date } {
  return half === 1
    ? { start: new Date(year, 0, 1), end: new Date(year, 5, 30) }
    : { start: new Date(year, 6, 1), end: new Date(year, 11, 31) };
}

function getFullYearDates(year: number): { start: Date; end: Date } {
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31),
  };
}

function getCurrentQuarter(): number {
  return Math.floor(new Date().getMonth() / 3) + 1;
}

function isSecondHalf(): boolean {
  return new Date().getMonth() >= 6;
}

function buildYearPeriodOptions(
  t: (key: string, opts?: Record<string, unknown>) => string,
  year: number
): PeriodOption[] {
  const q1 = getQuarterDates(year, 1);
  const q2 = getQuarterDates(year, 2);
  const q3 = getQuarterDates(year, 3);
  const q4 = getQuarterDates(year, 4);
  const h1 = getHalfDates(year, 1);
  const h2 = getHalfDates(year, 2);
  const fy = getFullYearDates(year);

  return [
    {
      id: `Q1-${year}`,
      title: t("common:period_q1", { year }),
      startDate: q1.start,
      endDate: q1.end,
    },
    {
      id: `Q2-${year}`,
      title: t("common:period_q2", { year }),
      startDate: q2.start,
      endDate: q2.end,
    },
    {
      id: `Q3-${year}`,
      title: t("common:period_q3", { year }),
      startDate: q3.start,
      endDate: q3.end,
    },
    {
      id: `Q4-${year}`,
      title: t("common:period_q4", { year }),
      startDate: q4.start,
      endDate: q4.end,
    },
    {
      id: `H1-${year}`,
      title: t("common:period_h1", { year }),
      startDate: h1.start,
      endDate: h1.end,
    },
    {
      id: `H2-${year}`,
      title: t("common:period_h2", { year }),
      startDate: h2.start,
      endDate: h2.end,
    },
    {
      id: `Y-${year}`,
      title: t("common:period_full_year", { year }),
      startDate: fy.start,
      endDate: fy.end,
    },
  ];
}

function buildPeriodOptions(
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodOption[] {
  const now = new Date();
  const year = now.getFullYear();
  const options = buildYearPeriodOptions(t, year);

  if (isSecondHalf()) {
    options.push(...buildYearPeriodOptions(t, year + 1));
  }

  return options;
}

function detectPeriodFromDates(
  startDate: Date,
  endDate: Date,
  periodOptions: PeriodOption[]
): string {
  for (const option of periodOptions) {
    if (
      option.startDate.getTime() === startDate.getTime() &&
      option.endDate.getTime() === endDate.getTime()
    ) {
      return option.id;
    }
  }
  return CUSTOM_PERIOD;
}

function getDefaultCurrentQuarterId(): string {
  const year = new Date().getFullYear();
  return `Q${getCurrentQuarter()}-${year}`;
}

function getCurrentQuarterDates(): { start: Date; end: Date } {
  const year = new Date().getFullYear();
  return getQuarterDates(year, getCurrentQuarter());
}

interface GoalEditModalProps extends ModalProps {
  goalId?: number;
  parentGoal?: ApiGoal;
  disableOwnerTypeChoice?: boolean;
  owner_employee?: ApiEmployeeShort;
  owner_department?: ApiDepartment;
  owner_team?: ApiTeam;
  owner_company?: ApiOrganization;
}

export function GoalEditModal({
  goalId,
  parentGoal,
  disableOwnerTypeChoice,
  owner_employee,
  owner_department,
  owner_team,
  owner_company,
  ...props
}: GoalEditModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { id } = props;
  const { close } = useModals();
  const navigate = useNavigate();
  const { canIEdit } = usePermissions();
  const requiredMessage = t("errors:field_required");
  const goalSchema = z
    .object({
      title: z.string().min(1, requiredMessage),
      description: z.string().nullable(),
      owner_type: z.enum(ApiGoalOwnerType, { error: requiredMessage }),
      owner_id: z.number().nullable(),
      assign_to_id: z.number().nullable(),
      parent_goal_id: z.number().nullable(),
      is_shared: z.boolean(),
      visible_to_ids: z.array(z.number()),
      start_date: z.date({ error: requiredMessage }),
      end_date: z.date({ error: requiredMessage }),
      status: z.enum(ApiGoalStatus, { error: requiredMessage }),
      confidence_level: z.enum(ApiGoalConfidenceLevel, { error: requiredMessage }),
      weight: z
        .number({ error: requiredMessage })
        .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
        .max(999.99, t("api_errors:maximum_value_must", { maximum: 999.99 }))
        .refine((value) => /^\d{1,3}(\.\d{1,2})?$/.test(value.toString()), {
          message: t("errors:invalid_format"),
        }),
      period: z.string().min(1, requiredMessage),
      weight_mode: z.string().min(1, requiredMessage),
    })
    .superRefine((data, context) => {
      if (data.owner_type !== ApiGoalOwnerType.company && data.owner_id === null) {
        context.addIssue({ code: "custom", path: ["owner_id"], message: requiredMessage });
      }

      if (
        [ApiGoalOwnerType.company, ApiGoalOwnerType.department, ApiGoalOwnerType.team].includes(
          data.owner_type
        ) &&
        data.assign_to_id === null
      ) {
        context.addIssue({ code: "custom", path: ["assign_to_id"], message: requiredMessage });
      }
    });

  const isEditMode = !!goalId;

  const canCreateCompanyGoal = canIEdit(ApiPermission.goals_company_manage);
  const canCreateDepartmentGoal =
    canIEdit(ApiPermission.goals_department_manage_all) ||
    canIEdit(ApiPermission.goals_department_manage_own);
  const canCreateTeamGoal =
    canIEdit(ApiPermission.goals_team_manage_all) || canIEdit(ApiPermission.goals_team_manage_own);

  const defaultOwnerType = owner_employee
    ? ApiGoalOwnerType.employee
    : owner_department
      ? ApiGoalOwnerType.department
      : owner_team
        ? ApiGoalOwnerType.team
        : owner_company
          ? ApiGoalOwnerType.company
          : undefined;

  const defaultOwnerId = owner_employee?.id ?? owner_department?.id ?? owner_team?.id ?? null;

  const hasOwnerModel = !!(owner_employee || owner_department || owner_team || owner_company);
  const shouldHideOwnerTypeChoice = (disableOwnerTypeChoice && hasOwnerModel) || isEditMode;
  const isEmployeeGoal =
    defaultOwnerType === ApiGoalOwnerType.employee && !!defaultOwnerId && disableOwnerTypeChoice;

  const { data: goal, isLoading: isGoalLoading } = useGoal(goalId ?? 0, isEditMode);
  const goalSave = useGoalSave();
  const employeeGoalCreate = useEmployeeGoalCreate();
  const goalsSearch = useGoalsSearchInline();

  const periodOptions = useMemo(() => buildPeriodOptions(t), [t]);
  const defaultQuarterDates = useMemo(() => getCurrentQuarterDates(), []);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(goalSchema),
    defaultValues: {
      title: "",
      description: "",
      owner_type: defaultOwnerType ?? ApiGoalOwnerType.employee,
      owner_id: defaultOwnerId ?? null,
      assign_to_id: null,
      parent_goal_id: parentGoal?.id ?? null,
      is_shared: true,
      visible_to_ids: [],
      start_date: parentGoal?.date_start ?? defaultQuarterDates.start,
      end_date: parentGoal?.date_end ?? defaultQuarterDates.end,
      status: ApiGoalStatus.active,
      confidence_level: ApiGoalConfidenceLevel.on_track,
      weight: 1,
      period: parentGoal ? CUSTOM_PERIOD : getDefaultCurrentQuarterId(),
      weight_mode: "default",
    },
  });

  const ownerType = watch("owner_type");
  const ownerId = watch("owner_id");
  const assignToId = watch("assign_to_id");
  const isShared = watch("is_shared");
  const selectedPeriod = watch("period");
  const weightMode = watch("weight_mode");

  const { data: employeeMe } = useEmployeeMe();

  const visibleToSearch = useEmployeeSearchInline(!isShared, {
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
    exclude_ids: employeeMe?.id ? [employeeMe.id] : [],
  });

  const employeeSearch = useEmployeeSearchInline(ownerType === ApiGoalOwnerType.employee, {
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  });
  const assignToSearch = useEmployeeSearchInline(
    ownerType === ApiGoalOwnerType.company ||
      ownerType === ApiGoalOwnerType.team ||
      ownerType === ApiGoalOwnerType.department,
    {
      employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
    }
  );
  const departmentSearch = useDepartmentsSearchInline(ownerType === ApiGoalOwnerType.department);
  const teamSearch = useTeamSearchInline(ownerType === ApiGoalOwnerType.team);
  const selectedDepartment = departmentSearch.data?.find((department) => department.id === ownerId);

  useEffect(() => {
    if (goal && isEditMode) {
      setValue("title", goal.title);
      setValue("description", goal.description ?? "");
      setValue("owner_type", goal.owner_type);
      const ownerId =
        goal.owner_employee?.id ?? goal.owner_department?.id ?? goal.owner_team?.id ?? null;
      if (ownerId) setValue("owner_id", ownerId);
      setValue("assign_to_id", goal.assign_to?.id ?? null);
      setValue("parent_goal_id", goal.parent_goal?.id ?? null);
      setValue("is_shared", goal.is_shared);
      setValue("visible_to_ids", goal.visible_to?.map((e) => e.id) ?? []);

      const startDate = new Date(goal.date_start);
      const endDate = new Date(goal.date_end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      setValue("start_date", startDate);
      setValue("end_date", endDate);

      const detectedPeriod = detectPeriodFromDates(startDate, endDate, periodOptions);
      setValue("period", detectedPeriod);

      setValue("status", goal.status);
      setValue("confidence_level", goal.confidence_level);
      const goalWeight = Number(goal.weight);
      setValue("weight", goalWeight);
      setValue("weight_mode", goalWeight !== 1 ? "custom" : "default");
    }
  }, [goal, isEditMode, setValue, periodOptions]);

  const getDefaultOwnerOption = () => {
    if (isEditMode && goal) {
      if (goal.owner_employee) {
        return {
          id: goal.owner_employee.id,
          title: renderEmployeeName(goal.owner_employee),
          avatarUrl: getEmployeeAvatarUrl(goal.owner_employee, "md"),
        };
      }
      if (goal.owner_department) {
        return {
          id: goal.owner_department.id,
          title: goal.owner_department.title,
        };
      }
      if (goal.owner_team) {
        return {
          id: goal.owner_team.id,
          title: goal.owner_team.title,
        };
      }
    } else {
      if (owner_employee) {
        return {
          id: owner_employee.id,
          title: renderEmployeeName(owner_employee),
          avatarUrl: getEmployeeAvatarUrl(owner_employee, "md"),
        };
      }
      if (owner_department) {
        return {
          id: owner_department.id,
          title: owner_department.title,
        };
      }
      if (owner_team) {
        return {
          id: owner_team.id,
          title: owner_team.title,
        };
      }
    }
    return null;
  };

  const defaultOwnerOption = getDefaultOwnerOption();

  const onSubmit = handleSubmit(async (data) => {
    const startDate = data.start_date;
    const endDate = data.end_date;

    const effectiveOwnerType = isEditMode && goal ? goal.owner_type : data.owner_type;
    const assignToId =
      effectiveOwnerType === ApiGoalOwnerType.employee
        ? data.owner_id
        : (data.assign_to_id ?? null);

    const payload: ApiGoalPayload = {
      title: data.title,
      description: data.description ?? null,
      owner_type: isEditMode && goal ? goal.owner_type : (data.owner_type as ApiGoalOwnerType),
      owner_id: data.owner_id ?? null,
      assign_to_id: assignToId,
      parent_goal_id: data.parent_goal_id ?? null,
      is_shared: data.is_shared,
      visible_to_ids: data.visible_to_ids ?? [],
      date_start: startDate,
      date_end: endDate,
      status: data.status as ApiGoalStatus,
      confidence_level: data.confidence_level as ApiGoalConfidenceLevel,
      weight: data.weight,
    };

    try {
      let savedGoal;

      if (isEditMode) {
        savedGoal = await goalSave.mutateAsync({ id: goalId, payload });
        analyticsService.trackEvent(analyticEvents.goals.goalEdited, {
          [analyticProperties.id]: savedGoal.id,
        });
      } else if (isEmployeeGoal) {
        savedGoal = await employeeGoalCreate.mutateAsync({
          employeeId: defaultOwnerId,
          payload,
        });
        analyticsService.trackEvent(analyticEvents.goals.goalCreated, {
          [analyticProperties.id]: savedGoal.id,
        });
        navigate(`/goals/${savedGoal.id}`);
      } else {
        savedGoal = await goalSave.mutateAsync({ payload });
        analyticsService.trackEvent(analyticEvents.goals.goalCreated, {
          [analyticProperties.id]: savedGoal.id,
        });
        navigate(`/goals/${savedGoal.id}`);
      }

      close(id);
    } catch {
      return;
    }
  });

  const handleOwnerTypeChange = (newOwnerType: ApiGoalOwnerType) => {
    setValue("owner_type", newOwnerType);
    setValue("owner_id", null);
    setValue("assign_to_id", null);
  };

  const getDefaultOptionForType = (type: ApiGoalOwnerType) => {
    if (defaultOwnerOption && defaultOwnerType === type) {
      return [defaultOwnerOption];
    }
    if (isEditMode && goal && goal.owner_type === type) {
      return defaultOwnerOption ? [defaultOwnerOption] : [];
    }
    return [];
  };

  const renderOwnerSelect = () => {
    const effectiveOwnerType = isEditMode && goal ? goal.owner_type : ownerType;

    switch (effectiveOwnerType) {
      case ApiGoalOwnerType.employee:
        return (
          <Controller
            control={control}
            name="owner_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("core:employee")}
                emptyTitle={t("common:select")}
                isLoading={employeeSearch.isLoading}
                showCaption={true}
                showAvatar={true}
                value={value}
                searchable
                isClearable
                onSearchOptions={(text) => employeeSearch.search(text)}
                searchOptions={employeeSearch.options}
                loadOptionsOnOpen
                options={getDefaultOptionForType(ApiGoalOwnerType.employee)}
                onChange={onChange}
                error={errors.owner_id?.message}
                required
              />
            )}
          />
        );
      case ApiGoalOwnerType.department:
        return (
          <Stack>
            <Controller
              control={control}
              name="owner_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("core:department")}
                  emptyTitle={t("common:select")}
                  isLoading={departmentSearch.isLoading}
                  value={value}
                  searchable
                  isClearable
                  onSearchOptions={(text) => departmentSearch.search(text)}
                  searchOptions={departmentSearch.options}
                  loadOptionsOnOpen
                  options={getDefaultOptionForType(ApiGoalOwnerType.department)}
                  onChange={(departmentId) => {
                    onChange(departmentId);

                    if (!getValues("assign_to_id")) {
                      const manager = departmentSearch.data?.find(
                        (department) => department.id === departmentId
                      )?.manager;
                      if (manager) setValue("assign_to_id", manager.id);
                    }
                  }}
                  error={errors.owner_id?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="assign_to_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:assign_to")}
                  emptyTitle={t("common:select")}
                  isLoading={assignToSearch.isLoading}
                  showCaption={true}
                  showAvatar={true}
                  value={value}
                  searchable
                  isClearable
                  onSearchOptions={(text) => assignToSearch.search(text)}
                  searchOptions={assignToSearch.options}
                  loadOptionsOnOpen
                  options={
                    goal?.assign_to?.id === assignToId
                      ? [
                          {
                            id: goal.assign_to.id,
                            title: renderEmployeeName(goal.assign_to),
                            avatarUrl: getEmployeeAvatarUrl(goal.assign_to, "md"),
                          },
                        ]
                      : selectedDepartment?.manager?.id === assignToId
                        ? [
                            {
                              id: selectedDepartment.manager.id,
                              title: renderEmployeeName(selectedDepartment.manager),
                              avatarUrl: getEmployeeAvatarUrl(selectedDepartment.manager, "md"),
                            },
                          ]
                        : []
                  }
                  onChange={onChange}
                  error={errors.assign_to_id?.message}
                  required
                />
              )}
            />
          </Stack>
        );
      case ApiGoalOwnerType.team:
        return (
          <Stack>
            <Controller
              control={control}
              name="owner_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("core:team")}
                  emptyTitle={t("common:select")}
                  isLoading={teamSearch.isLoading}
                  value={value}
                  searchable
                  isClearable
                  onSearchOptions={(text) => teamSearch.search(text)}
                  searchOptions={teamSearch.options}
                  loadOptionsOnOpen
                  options={getDefaultOptionForType(ApiGoalOwnerType.team)}
                  onChange={onChange}
                  error={errors.owner_id?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="assign_to_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:assign_to")}
                  emptyTitle={t("common:select")}
                  isLoading={assignToSearch.isLoading}
                  showCaption={true}
                  showAvatar={true}
                  value={value}
                  searchable
                  isClearable
                  onSearchOptions={(text) => assignToSearch.search(text)}
                  searchOptions={assignToSearch.options}
                  loadOptionsOnOpen
                  options={
                    isEditMode && goal?.assign_to
                      ? [
                          {
                            id: goal.assign_to.id,
                            title: renderEmployeeName(goal.assign_to),
                            avatarUrl: getEmployeeAvatarUrl(goal.assign_to, "md"),
                          },
                        ]
                      : []
                  }
                  onChange={onChange}
                  error={errors.assign_to_id?.message}
                  required
                />
              )}
            />
          </Stack>
        );
      case ApiGoalOwnerType.company:
        return (
          <Controller
            control={control}
            name="assign_to_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("common:assign_to")}
                emptyTitle={t("common:select")}
                isLoading={assignToSearch.isLoading}
                showCaption={true}
                showAvatar={true}
                value={value}
                searchable
                isClearable
                onSearchOptions={(text) => assignToSearch.search(text)}
                searchOptions={assignToSearch.options}
                loadOptionsOnOpen
                options={
                  isEditMode && goal?.assign_to
                    ? [
                        {
                          id: goal.assign_to.id,
                          title: renderEmployeeName(goal.assign_to),
                          avatarUrl: getEmployeeAvatarUrl(goal.assign_to, "md"),
                        },
                      ]
                    : []
                }
                onChange={onChange}
                error={errors.assign_to_id?.message}
                required
              />
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={isEditMode ? t("goals:edit_goal") : t("goals:create_goal")}
      variant={"side"}
      layout={"stretch"}
      withCloser
      closeByEscEnabled
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={onSubmit}
            isLoading={goalSave.isPending || employeeGoalCreate.isPending || isGoalLoading}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
      {...props}
    >
      {isEditMode && isGoalLoading ? (
        <Stack className="py-8" justify="center">
          <span className="text-secondary">{t("common:loading")}</span>
        </Stack>
      ) : (
        <form onSubmit={onSubmit}>
          <Stack>
            <Input
              {...register("title")}
              label={t("common:title")}
              required
              error={errors.title?.message}
              size={"lg"}
            />

            <Controller
              render={({ field: { value, onChange } }) => (
                <RichEditor
                  label={t("common:description")}
                  likeTextArea={true}
                  hideToolbar={true}
                  value={value || ""}
                  onChange={onChange}
                  error={errors.description?.message}
                  placeholder={""}
                />
              )}
              control={control}
              name={"description"}
            />

            {!shouldHideOwnerTypeChoice && (
              <>
                <Title
                  size={"xs"}
                  header={t("common:choose_type")}
                  paddingTop
                  paddingBottom
                ></Title>
                <Controller
                  control={control}
                  name="owner_type"
                  render={({ field }) => (
                    <Stack gap={"md"}>
                      <Stack>
                        <Radio
                          value={ApiGoalOwnerType.employee}
                          checked={field.value === ApiGoalOwnerType.employee}
                          onChange={() => handleOwnerTypeChange(ApiGoalOwnerType.employee)}
                          title={t("goals:employee_goal")}
                          caption={t("goals:employee_goal_caption")}
                        />
                        {field.value === ApiGoalOwnerType.employee && (
                          <Box className="ml-9">{renderOwnerSelect()}</Box>
                        )}
                      </Stack>

                      {canCreateDepartmentGoal && (
                        <Stack>
                          <Radio
                            value={ApiGoalOwnerType.department}
                            checked={field.value === ApiGoalOwnerType.department}
                            onChange={() => handleOwnerTypeChange(ApiGoalOwnerType.department)}
                            title={t("goals:department_goal")}
                            caption={t("goals:department_goal_caption")}
                          />
                          {field.value === ApiGoalOwnerType.department && (
                            <Box className="ml-9">{renderOwnerSelect()}</Box>
                          )}
                        </Stack>
                      )}

                      {canCreateTeamGoal && (
                        <Stack>
                          <Radio
                            value={ApiGoalOwnerType.team}
                            checked={field.value === ApiGoalOwnerType.team}
                            onChange={() => handleOwnerTypeChange(ApiGoalOwnerType.team)}
                            title={t("goals:team_goal")}
                            caption={t("goals:team_goal_caption")}
                          />
                          {field.value === ApiGoalOwnerType.team && (
                            <Box className="ml-9">{renderOwnerSelect()}</Box>
                          )}
                        </Stack>
                      )}

                      {canCreateCompanyGoal && (
                        <Stack>
                          <Radio
                            value={ApiGoalOwnerType.company}
                            checked={field.value === ApiGoalOwnerType.company}
                            onChange={() => handleOwnerTypeChange(ApiGoalOwnerType.company)}
                            title={t("goals:company_goal")}
                            caption={t("goals:company_goal_caption")}
                          />
                          {field.value === ApiGoalOwnerType.company && (
                            <Box className="ml-9">{renderOwnerSelect()}</Box>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                />
              </>
            )}

            {shouldHideOwnerTypeChoice && renderOwnerSelect()}

            <Title size={"xs"} header={t("goals:timeline")} paddingTop paddingBottom></Title>

            <Controller
              control={control}
              name="period"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:period")}
                  emptyTitle={t("common:select")}
                  value={value}
                  options={[
                    ...periodOptions.map((o) => ({ id: o.id, title: o.title })),
                    { id: CUSTOM_PERIOD, title: t("common:custom_period") },
                  ]}
                  onChange={(v) => {
                    onChange(v);
                    if (v && v !== CUSTOM_PERIOD) {
                      const option = periodOptions.find((o) => o.id === v);
                      if (option) {
                        setValue("start_date", option.startDate);
                        setValue("end_date", option.endDate);
                      }
                    }
                  }}
                />
              )}
            />

            {selectedPeriod === CUSTOM_PERIOD && (
              <>
                <Controller
                  control={control}
                  name="start_date"
                  render={({ field: { value, onChange } }) => (
                    <Datepicker
                      value={value}
                      onChange={onChange}
                      required
                      label={t("common:start_date")}
                      error={errors.start_date?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="end_date"
                  render={({ field: { value, onChange } }) => (
                    <Datepicker
                      value={value}
                      onChange={onChange}
                      required
                      label={t("common:due_date")}
                      error={errors.end_date?.message}
                    />
                  )}
                />
              </>
            )}

            <Title size={"xs"} header={t("common:configuration")} paddingTop paddingBottom></Title>

            <Controller
              control={control}
              name="parent_goal_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("goals:parent_goal")}
                  emptyTitle={t("common:select")}
                  isLoading={goalsSearch.isLoading}
                  value={value}
                  searchable
                  isClearable
                  showAvatar
                  showCaption
                  onSearchOptions={(text) => goalsSearch.search(text)}
                  searchOptions={goalsSearch.options}
                  loadOptionsOnOpen
                  options={
                    isEditMode && goal?.parent_goal
                      ? [{ id: goal.parent_goal.id, title: goal.parent_goal.title }]
                      : parentGoal
                        ? [{ id: parentGoal.id, title: parentGoal.title }]
                        : []
                  }
                  onChange={onChange}
                  error={errors.parent_goal_id?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="is_shared"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:visibility")}
                  emptyTitle={t("common:select")}
                  options={[
                    { id: "true", title: t("goals:privacy_public") },
                    { id: "false", title: t("goals:privacy_private") },
                  ]}
                  value={value ? "true" : "false"}
                  onChange={(v) => {
                    onChange(v === "true");
                    if (v === "true") {
                      setValue("visible_to_ids", []);
                    }
                  }}
                  error={errors.is_shared?.message}
                />
              )}
            />

            {!isShared && (
              <Controller
                control={control}
                name="visible_to_ids"
                render={({ field: { value, onChange } }) => (
                  <Multiselect
                    label={t("common:visible_to")}
                    emptyTitle={t("common:select")}
                    isLoading={visibleToSearch.isLoading}
                    showCaption={true}
                    showAvatar={true}
                    values={value}
                    searchable
                    onSearchOptions={(text) => visibleToSearch.search(text)}
                    searchOptions={visibleToSearch.options}
                    loadOptionsOnOpen
                    options={[]}
                    onChange={onChange}
                  />
                )}
              />
            )}

            <Controller
              control={control}
              name="weight_mode"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("goals:weight")}
                  emptyTitle={t("common:select")}
                  value={value}
                  options={[
                    { id: "default", title: t("common:weight_default") },
                    { id: "custom", title: t("common:weight_custom") },
                  ]}
                  onChange={(v) => {
                    onChange(v);
                    if (v === "default") {
                      setValue("weight", 1);
                    }
                  }}
                />
              )}
            />

            {weightMode === "custom" && (
              <Input
                {...register("weight", {
                  setValueAs: (value) => (value === "" ? undefined : Number(value)),
                })}
                type="number"
                step={0.01}
                label={t("goals:weight")}
                error={errors.weight?.message}
              />
            )}
          </Stack>
        </form>
      )}
    </Modal>
  );
}
